import React, { useContext, useState, useEffect } from 'react';
import { APIContext } from '../../../Contexts/APIContext';
import DropdownViewModel from '../../../Contexts/ViewModels/DropdownViewModel';
import CallTableViewModel from '../../../ViewModels/CallTableViewModel';
import ScorecardAnswersViewModel from './ViewModels/ScorecardAnswersViewModel';
import { useSnackbar } from 'notistack';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';

interface IFiltersDialogProps {
    open: boolean
    setOpen: (value: boolean) => void
    callTableViewModel: CallTableViewModel
    setCallTableViewModel: (value: CallTableViewModel) => void
}

export default function ScorecardAnswersDialog(props: IFiltersDialogProps) {
    const { get } = useContext(APIContext);
    const [templateOptions, setTemplateOptions] = useState<DropdownViewModel[]>([]);
    const [sectionOptions, setSectionOptions] = useState<DropdownViewModel[]>([]);
    const [questionOptions, setQuestionOptions] = useState<DropdownViewModel[]>([]);
    const [answerOptions, setAnswerOptions] = useState<DropdownViewModel[]>([]);
    const [scorecardFilters, setScorecardFilters] = useState<ScorecardAnswersViewModel>(props.callTableViewModel.filters.scorecardAnswer);
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        if (props.open) {
            setScorecardFilters(props.callTableViewModel.filters.scorecardAnswer);
        }
    }, [props.open])

    useEffect(() => {
        getTemplateOptions();
    }, []);

    useEffect(() => {
        HandleSelect("sectionId", 0);
        if (scorecardFilters.templateId !== 0) getSectionOptions();
    }, [scorecardFilters.templateId]);

    useEffect(() => {
        HandleSelect("questionId", 0);
        if (scorecardFilters.templateId !== 0 && scorecardFilters.sectionId !== 0) getQuestionOptions();
    }, [scorecardFilters.sectionId]);

    useEffect(() => {
        HandleSelectAnswer("sectionId", '');
        if (scorecardFilters.sectionId !== 0 && scorecardFilters.questionId !== 0) getAnswerOptions();
    }, [scorecardFilters.questionId]);

    //#region database calls
    const getTemplateOptions = () => {
        get('ScorecardManagement', `GetTemplateOptions?client=${props.callTableViewModel.filters.client}`, (data) => {
            setTemplateOptions(data);
        });
    }

    const getSectionOptions = () => {
        get('ScorecardManagement', `GetSectionOptions?templateId=` + scorecardFilters.templateId, (data) => {
            setSectionOptions(data);
        });
    }

    const getQuestionOptions = () => {
        get('ScorecardManagement', `GetQuestionOptions?sectionId=` + scorecardFilters.sectionId, (data) => {
            setQuestionOptions(data)
        });
    }

    const getAnswerOptions = () => {
        get('ScorecardManagement', `GetAnswerOptions?questionId=` + scorecardFilters.questionId, (data) => {
            setAnswerOptions(data)
        });
    }
    //#endregion

    const Confirm = () => {
        if (scorecardFilters.templateId !== 0 && scorecardFilters.sectionId !== 0 && scorecardFilters.questionId !== 0 && scorecardFilters.answer) {
            const newCallTableViewModel = { ...props.callTableViewModel };

            newCallTableViewModel.filters.scorecardAnswer = scorecardFilters;

            props.setCallTableViewModel({
                ...newCallTableViewModel
            })

            props.setOpen(false);
        } else {
            enqueueSnackbar("All values must be selected to confirm", { variant: 'warning' });
        }
    }

    const HandleSelect = (key: string, value: number, questionText?: string) => {
        setScorecardFilters({
            ...scorecardFilters,
            [key]: value
        });
    }
    const HandleSelectAnswer = (key: string, value: string) => {
        setScorecardFilters({
            ...scorecardFilters,
            answer: value
        });
    }

    return (
        <Dialog onClose={() => props.setOpen(false)} open={props.open} fullWidth maxWidth="sm">
            <DialogTitle>QA Scorecard Answers</DialogTitle>
            <DialogContent>
                {templateOptions.length > 0 ?
                    <Grid container height="100%">
                        <Grid size={12}>
                            <InputLabel>Template</InputLabel>
                            <TextField
                                select
                                name="templateId"
                                value={scorecardFilters.templateId}
                                onChange={(e) => HandleSelect(e.target.name, Number(e.target.value))}
                                fullWidth
                            >
                                {templateOptions.length > 0 && templateOptions.map((template: DropdownViewModel) => {
                                    return (<MenuItem key={template.id} value={template.id}>{template.label}</MenuItem>);
                                })}
                            </TextField>
                        </Grid>
                        {scorecardFilters.templateId !== 0 &&
                            <Grid size={12}>
                                <InputLabel>Section</InputLabel>
                                <TextField
                                    select
                                    name="sectionId"
                                    value={scorecardFilters.sectionId}
                                    onChange={(e) => HandleSelect(e.target.name, Number(e.target.value))}
                                    fullWidth
                                    disabled={scorecardFilters.templateId === 0}
                                >
                                    {sectionOptions.length > 0 && sectionOptions.map((section: DropdownViewModel) => {
                                        return (<MenuItem key={section.id} value={section.id}>{section.label}</MenuItem>);
                                    })}
                                </TextField>
                            </Grid>
                        }
                        {scorecardFilters.sectionId !== 0 &&
                            <Grid size={12}>
                                <InputLabel>Question</InputLabel>
                                <TextField
                                    select
                                    name="questionId"
                                    value={scorecardFilters.questionId}
                                    onChange={(e) => HandleSelect(e.target.name, Number(e.target.value), questionOptions.filter(f => f.id == Number(e.target.value))[0].label)}
                                    fullWidth
                                    disabled={scorecardFilters.sectionId === 0}
                                >
                                    {questionOptions.length > 0 && questionOptions.map((question: DropdownViewModel) => {
                                        return (<MenuItem key={question.id} value={question.id}>{question.label}</MenuItem>);
                                    })}
                                </TextField>
                            </Grid>
                        }
                        {scorecardFilters.questionId !== 0 &&
                            <Grid size={12}>
                                <InputLabel>Answer</InputLabel>
                                <TextField
                                    select
                                    name="answer"
                                    value={scorecardFilters.answer}
                                    onChange={(e) => HandleSelectAnswer(e.target.name, e.target.value)}
                                    fullWidth
                                    disabled={scorecardFilters.questionId === 0}
                                >
                                    {answerOptions.length > 0 && answerOptions.map((answer: DropdownViewModel) => {
                                        return (<MenuItem key={answer.id} value={answer.label}>{answer.label}</MenuItem>);
                                    })}
                                </TextField>
                            </Grid>
                        }
                    </Grid>
                    :
                    <p>This AI Filter Is Not Used By This Client</p>
                }
            </DialogContent>
            <DialogActions>
                <Button onClick={() => props.setOpen(false)}>
                    Cancel
                </Button>
                {templateOptions.length > 0 &&
                    <Button onClick={Confirm} color="primary">
                        Confirm
                    </Button>
                }
            </DialogActions>
        </Dialog >
    )
}